import React, { useState, useEffect, ChangeEvent, FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient, useQuery } from "react-query";
import { Users, ChevronDown, Sparkles, X, Briefcase } from "lucide-react";
import { recruitingAPI } from "../services/recruitingApi";
import TagInput from "./TagInput";
import TagSuggestionsModal from "./TagSuggestionsModal";
import api from "../services/api";
import "./ModalFormShared.css";
import "./JobForm.css";

interface JobData {
  id?: string | number;
  title?: string;
  department?: string;
  location?: string;
  employment_type?: string;
  seniority?: string;
  skills?: string[] | string;
  description?: string;
  status?: string;
  tags?: string | string[];
  customer_rate?: string | number;
  engineer_rate?: string | number;
  customer_id?: string | number;
  responsible_sales_id?: string | number;
  responsible_recruiter_id?: string | number;
  open_for_recruiters?: boolean;
  import_source_type?: string;
  import_source_ref?: string;
  main_skill?: string;
  priority?: string;
  start_date?: string;
  duration_months?: number | string;
  work_model?: string;
  positions?: number | string;
  deadline?: string;
}

interface JobFormProps {
  job?: JobData | null;
  onSave: () => void;
  onCancel: () => void;
  onDelete?: (jobId: string | number) => void;
  variant?: "modal" | "inline";
}

interface FormData {
  title: string;
  department: string;
  location: string;
  employment_type: string;
  seniority: string;
  skills: string[];
  description: string;
  status: string;
  tags: string[];
  customer_rate: string;
  engineer_rate: string;
  customer_id: string;
  responsible_sales_id: string;
  responsible_recruiter_id: string;
  open_for_recruiters: boolean;
  main_skill: string;
  priority: string;
  start_date: string;
  duration_months: string;
  work_model: string;
  positions: string;
  deadline: string;
}

interface FormErrors {
  [key: string]: string;
}

interface User {
  id: number;
  username: string;
  email: string;
}

interface Customer {
  id: number;
  name?: string;
  company_name?: string;
}

interface UsersResponse {
  users?: User[];
}

interface CustomersResponse {
  data?: Customer[];
}

interface ConfigResponse {
  data?: {
    value?: string;
  };
}

interface ExtendedDescription {
  enhanced?: {
    enhancedDescription?: string;
    description?: string;
  };
}

interface TagsResponse {
  success?: boolean;
  tags?: { tag: string; reason?: string }[];
}

const INITIAL_FORM_DATA: FormData = {
  title: "",
  department: "",
  location: "",
  employment_type: "",
  seniority: "",
  skills: [],
  description: "",
  status: "role_in_preparation",
  tags: [],
  customer_rate: "",
  engineer_rate: "",
  customer_id: "",
  responsible_sales_id: "",
  responsible_recruiter_id: "",
  open_for_recruiters: false,
  main_skill: "",
  priority: "Medium",
  start_date: "",
  duration_months: "",
  work_model: "",
  positions: "1",
  deadline: "",
};

const JobForm: React.FC<JobFormProps> = ({
  job,
  onSave,
  onCancel,
  onDelete,
  variant = "modal",
}) => {
  const { t } = useTranslation(["recruiting", "common"]);
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<FormData>({ ...INITIAL_FORM_DATA });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isExtendingDescription, setIsExtendingDescription] = useState(false);
  const [isGeneratingTags, setIsGeneratingTags] = useState(false);
  const [tagSuggestions, setTagSuggestions] = useState<
    { tag: string; reason?: string }[]
  >([]);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [tagInputValue, setTagInputValue] = useState("");
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    new Set(),
  );

  // Fetch agency mode setting
  const { data: agencyModeData } = useQuery<ConfigResponse>(
    ["system-config", "agency_mode"],
    () =>
      recruitingAPI.getSystemConfig("agency_mode") as Promise<ConfigResponse>,
    {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      retryDelay: 1000,
      onError: (error: Error) => {
        console.error("Error fetching agency mode:", error);
      },
    },
  );

  const isAgencyMode = agencyModeData?.data?.value === "true";

  // Fetch customers if agency mode is enabled
  const { data: customersData } = useQuery<CustomersResponse>(
    ["customers"],
    () =>
      recruitingAPI.getCustomers({
        status: "active",
      }) as Promise<CustomersResponse>,
    {
      enabled: isAgencyMode,
      staleTime: 2 * 60 * 1000,
    },
  );

  const customers = customersData?.data || [];

  // Fetch sales users
  const { data: salesUsersData } = useQuery<UsersResponse>(
    ["users", "sales"],
    () =>
      recruitingAPI.getUsersByRole(
        "sales",
      ) as unknown as Promise<UsersResponse>,
    {
      staleTime: 5 * 60 * 1000,
    },
  );

  const salesUsers = salesUsersData?.users || [];

  // Fetch recruiter users
  const { data: recruitersData } = useQuery<UsersResponse>(
    ["users", "recruiter"],
    () =>
      recruitingAPI.getUsersByRole(
        "recruiter",
      ) as unknown as Promise<UsersResponse>,
    {
      staleTime: 5 * 60 * 1000,
    },
  );

  const recruiters = recruitersData?.users || [];

  const parseSkills = (
    skillsValue: string[] | string | undefined,
  ): string[] => {
    if (Array.isArray(skillsValue)) return skillsValue;
    if (!skillsValue) return [];
    if (typeof skillsValue === "string") {
      try {
        return JSON.parse(skillsValue);
      } catch {
        return skillsValue
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
    }
    return [];
  };

  const parseTags = (tagsValue: string | string[] | undefined): string[] => {
    if (Array.isArray(tagsValue)) return tagsValue;
    if (!tagsValue) return [];
    if (typeof tagsValue === "string") {
      try {
        const parsed = JSON.parse(tagsValue);
        if (Array.isArray(parsed)) return parsed;
      } catch {
        // comma-separated fallback
      }
      return tagsValue
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean);
    }
    return [];
  };

  // Initialize form data when job prop changes
  useEffect(() => {
    if (job) {
      setFormData({
        title: job.title || "",
        department: job.department || "",
        location: job.location || "",
        employment_type: job.employment_type || "",
        seniority: job.seniority || "",
        skills: parseSkills(job.skills),
        description: job.description || "",
        status: job.status || "role_in_preparation",
        tags: parseTags(job.tags),
        customer_rate: job.customer_rate?.toString() || "",
        engineer_rate: job.engineer_rate?.toString() || "",
        customer_id: job.customer_id?.toString() || "",
        responsible_sales_id: job.responsible_sales_id?.toString() ?? "",
        responsible_recruiter_id:
          job.responsible_recruiter_id?.toString() ?? "",
        open_for_recruiters: job.open_for_recruiters || false,
        main_skill: job.main_skill || "",
        priority: job.priority || "Medium",
        start_date: job.start_date || "",
        duration_months: job.duration_months?.toString() || "",
        work_model: job.work_model || "",
        positions: job.positions?.toString() || "1",
        deadline: job.deadline || "",
      });
    }
  }, [job]);

  const isEditing = !!job?.id;

  // Create/Update job mutation
  const saveJobMutation = useMutation<unknown, Error, Record<string, unknown>>(
    (data) => {
      if (isEditing) {
        return recruitingAPI.updateJob(job?.id, data);
      }
      return recruitingAPI.createJob(data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("jobs");
        if (isEditing) {
          queryClient.invalidateQueries(["job", job?.id]);
        }
        onSave();
      },
      onError: (error: Error) => {
        console.error("Error saving job:", error);
        setErrors({ general: t("jobs.form.validation.saveFailed") });
      },
    },
  );

  // Extend description mutation
  const extendDescriptionMutation = useMutation<ExtendedDescription, Error>(
    async () => {
      if (!job?.id) {
        throw new Error(t("jobs.form.validation.saveFirstForAi"));
      }

      const result = (await recruitingAPI.extendJobDescription(
        job.id,
        {},
      )) as { data: ExtendedDescription };
      return result.data;
    },
    {
      onSuccess: (result) => {
        if (result?.enhanced) {
          const enhancedDescription =
            result.enhanced.enhancedDescription ||
            result.enhanced.description ||
            "";
          setFormData((prev) => ({
            ...prev,
            description: enhancedDescription,
          }));
        }
        setIsExtendingDescription(false);
      },
      onError: (error: Error) => {
        console.error("Error extending description:", error);
        setIsExtendingDescription(false);
        setErrors({
          description: error.message || t("jobs.form.validation.saveFailed"),
        });
      },
    },
  );

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleExtendDescription = async () => {
    if (!job?.id) {
      setErrors({ description: t("jobs.form.validation.saveFirstForAi") });
      return;
    }

    setIsExtendingDescription(true);
    setErrors((prev) => ({ ...prev, description: "" }));
    extendDescriptionMutation.mutate();
  };

  const handleGenerateTags = async () => {
    setIsGeneratingTags(true);
    setErrors((prev) => ({ ...prev, tags: "" }));

    try {
      const content = {
        title: formData.title,
        department: formData.department,
        location: formData.location,
        employment_type: formData.employment_type,
        seniority: formData.seniority,
        description: formData.description,
        skills: formData.skills,
        customer_rate: formData.customer_rate,
        engineer_rate: formData.engineer_rate,
      };

      const response = (await api.openai.generateTags(
        JSON.stringify(content),
        "job",
        formData.tags,
      )) as TagsResponse;

      if (response.success && response.tags && response.tags.length > 0) {
        setTagSuggestions(response.tags);
        setShowTagSuggestions(true);
      } else {
        setErrors((prev) => ({
          ...prev,
          tags: t("jobs.form.validation.noTagSuggestions"),
        }));
      }
    } catch (error) {
      console.error("Error generating tags:", error);
      setErrors((prev) => ({
        ...prev,
        tags: t("jobs.form.validation.tagsFailed"),
      }));
    } finally {
      setIsGeneratingTags(false);
    }
  };

  const handleAddSuggestedTags = (selectedTags: string[]) => {
    const newTags = [...formData.tags, ...selectedTags];
    const uniqueTags = [...new Set(newTags)];
    setFormData((prev) => ({ ...prev, tags: uniqueTags }));
    setTagSuggestions([]);
  };

  const getJobContext = () =>
    JSON.stringify({
      title: formData.title,
      department: formData.department,
      description: formData.description,
      seniority: formData.seniority,
      employment_type: formData.employment_type,
      skills: formData.skills,
    });

  const extractAiText = (response: unknown): string => {
    const r = response as Record<string, unknown>;
    const result = r?.result as Record<string, unknown> | string | undefined;
    if (typeof result === "string") return result.trim();
    if (result && typeof result === "object") {
      const content = (result as Record<string, unknown>).content;
      if (typeof content === "string") return content.trim();
    }
    return "";
  };

  const suggestMainSkillMutation = useMutation<unknown, Error>(
    () =>
      api.openai.generateText(
        `Based on this job posting, what is the single most important primary skill or technology? Answer with ONLY the skill name (1-3 words, e.g. "React", "Azure", "SAP", "Java"). No explanation.\n\nJob: ${getJobContext()}`,
      ),
    {
      onSuccess: (response) => {
        const skill = extractAiText(response);
        if (skill) {
          setFormData((prev) => ({ ...prev, main_skill: skill }));
        } else {
          setErrors((prev) => ({
            ...prev,
            main_skill: t("jobs.form.validation.aiSuggestionFailed"),
          }));
        }
      },
      onError: (error: Error) => {
        console.error("Error suggesting main skill:", error);
        setErrors((prev) => ({
          ...prev,
          main_skill: t("jobs.form.validation.aiSuggestionFailed"),
        }));
      },
    },
  );

  const suggestSkillsMutation = useMutation<unknown, Error>(
    () =>
      api.openai.generateText(
        `Based on this job posting, suggest 5-8 relevant technical skills. Return ONLY a comma-separated list of skill names (1-3 words each). No numbering, no explanation.\n\nExisting skills to exclude: ${formData.skills.join(", ")}\n\nJob: ${getJobContext()}`,
      ),
    {
      onSuccess: (response) => {
        const skillText = extractAiText(response);
        if (skillText) {
          const suggestedSkills = skillText
            .split(",")
            .map((s) => s.trim())
            .filter((s) => s && !formData.skills.includes(s));
          if (suggestedSkills.length > 0) {
            const newSkills = [...formData.skills, ...suggestedSkills];
            setFormData((prev) => ({ ...prev, skills: newSkills }));
          }
        } else {
          setErrors((prev) => ({
            ...prev,
            skills: t("jobs.form.validation.aiSuggestionFailed"),
          }));
        }
      },
      onError: (error: Error) => {
        console.error("Error suggesting skills:", error);
        setErrors((prev) => ({
          ...prev,
          skills: t("jobs.form.validation.aiSuggestionFailed"),
        }));
      },
    },
  );

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const value = tagInputValue.trim();
      if (value && !formData.tags.includes(value)) {
        setFormData((prev) => ({ ...prev, tags: [...prev.tags, value] }));
      }
      setTagInputValue("");
    }
  };

  const toggleSection = (sectionId: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const getSectionSummary = (sectionId: string): string => {
    const values: string[] = [];
    if (sectionId === "basic-info") {
      if (formData.priority && formData.priority !== "Medium")
        values.push(formData.priority);
      if (formData.employment_type) values.push(formData.employment_type);
      if (formData.seniority) values.push(formData.seniority);
      if (formData.location) values.push(formData.location);
      if (formData.department) values.push(formData.department);
    } else if (sectionId === "timeline") {
      if (formData.work_model) values.push(formData.work_model);
      if (formData.positions && formData.positions !== "1")
        values.push(`${formData.positions} pos`);
      if (formData.duration_months)
        values.push(`${formData.duration_months}mo`);
      if (formData.start_date) values.push(formData.start_date);
    }
    return values.join(", ");
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = t("jobs.form.validation.titleRequired");
    }

    if (
      formData.customer_rate &&
      Number.isNaN(Number.parseFloat(formData.customer_rate))
    ) {
      newErrors.customer_rate = t("jobs.form.validation.customerRateNumber");
    }

    if (
      formData.engineer_rate &&
      Number.isNaN(Number.parseFloat(formData.engineer_rate))
    ) {
      newErrors.engineer_rate = t("jobs.form.validation.engineerRateNumber");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData: Record<string, unknown> = {
      ...formData,
      customer_rate: formData.customer_rate
        ? Number.parseFloat(formData.customer_rate)
        : null,
      engineer_rate: formData.engineer_rate
        ? Number.parseFloat(formData.engineer_rate)
        : null,
      customer_id: formData.customer_id
        ? Number.parseInt(formData.customer_id, 10)
        : null,
      responsible_sales_id: formData.responsible_sales_id
        ? Number.parseInt(formData.responsible_sales_id, 10)
        : null,
      responsible_recruiter_id: formData.responsible_recruiter_id
        ? Number.parseInt(formData.responsible_recruiter_id, 10)
        : null,
      open_for_recruiters: formData.open_for_recruiters,
      skills: Array.isArray(formData.skills) ? formData.skills : [],
      tags: formData.tags,
      duration_months:
        formData.duration_months !== ""
          ? Number.parseInt(formData.duration_months, 10)
          : null,
      positions:
        formData.positions !== ""
          ? Number.parseInt(formData.positions, 10)
          : 1,
      start_date: formData.start_date || null,
      deadline: formData.deadline || null,
      ...(job?.import_source_type && {
        import_source_type: job.import_source_type,
        import_source_ref: job.import_source_ref,
      }),
    };

    saveJobMutation.mutate(submitData);
  };

  const handleDelete = () => {
    if (
      job &&
      job.id &&
      window.confirm(t("jobs.form.confirmDelete", { title: job.title }))
    ) {
      onDelete?.(job.id);
    }
  };

  const renderAiButton = (
    onClick: () => void,
    isLoading: boolean,
    title: string,
  ) => (
    <button
      type="button"
      className={`btn-ai-icon ${isLoading ? "loading" : ""}`}
      onClick={onClick}
      disabled={isLoading}
      title={isLoading ? "AI is thinking..." : title}
    >
      {isLoading ? (
        <span className="ai-spinner" />
      ) : (
        <Sparkles size={14} />
      )}
    </button>
  );

  const isCollapsed = (sectionId: string) => collapsedSections.has(sectionId);

  const renderCustomerSelect = () => (
    <div className="form-group">
      <label className="form-label" htmlFor="customer_id">
        {t("jobs.form.fields.customer")}{" "}
        <a
          href="/customers"
          target="_blank"
          rel="noopener noreferrer"
          className="customer-link"
          onClick={(e) => {
            e.preventDefault();
            window.open("/customers", "_blank");
          }}
        >
          <Users size={12} />
        </a>
      </label>
      <select
        id="customer_id"
        name="customer_id"
        className="form-select"
        value={formData.customer_id}
        onChange={handleInputChange}
      >
        <option value="">{t("jobs.form.labels.noCustomer")}</option>
        {customers.map((customer) => (
          <option key={customer.id} value={customer.id}>
            {customer.company_name || customer.name}
            {customer.company_name && customer.name && ` (${customer.name})`}
          </option>
        ))}
      </select>
    </div>
  );

  const renderPrioritySelect = () => (
    <div className="form-group">
      <label className="form-label" htmlFor="priority">
        {t("jobs.form.fields.priority")}
      </label>
      <select
        id="priority"
        name="priority"
        className="form-select"
        value={formData.priority}
        onChange={handleInputChange}
      >
        <option value="Low">Low</option>
        <option value="Medium">Medium</option>
        <option value="High">High</option>
        <option value="Urgent">Urgent</option>
      </select>
    </div>
  );

  const renderEmploymentTypeSelect = () => (
    <div className="form-group">
      <label className="form-label" htmlFor="employment_type">
        {t("jobs.form.fields.employmentType")}
      </label>
      <select
        id="employment_type"
        name="employment_type"
        className="form-select"
        value={formData.employment_type}
        onChange={handleInputChange}
      >
        <option value="">{t("jobs.form.selects.selectType")}</option>
        <option value="Full-time">{t("jobs.employmentType.fullTime")}</option>
        <option value="Part-time">{t("jobs.employmentType.partTime")}</option>
        <option value="Contract">{t("jobs.employmentType.contract")}</option>
        <option value="Freelance">{t("jobs.employmentType.freelance")}</option>
        <option value="Remote">{t("jobs.employmentType.remote")}</option>
      </select>
    </div>
  );

  const renderSenioritySelect = () => (
    <div className="form-group">
      <label className="form-label" htmlFor="seniority">
        {t("jobs.form.fields.seniority")}
      </label>
      <select
        id="seniority"
        name="seniority"
        className="form-select"
        value={formData.seniority}
        onChange={handleInputChange}
      >
        <option value="">{t("jobs.form.selects.selectLevel")}</option>
        <option value="Junior">{t("jobs.seniority.junior")}</option>
        <option value="Mid">{t("jobs.seniority.mid")}</option>
        <option value="Senior">{t("jobs.seniority.senior")}</option>
        <option value="Lead">{t("jobs.seniority.lead")}</option>
        <option value="Principal">{t("jobs.seniority.principal")}</option>
      </select>
    </div>
  );

  const renderLocationInput = () => (
    <div className="form-group">
      <label className="form-label" htmlFor="location">
        {t("jobs.form.fields.location")}
      </label>
      <input
        type="text"
        id="location"
        name="location"
        className="form-input"
        value={formData.location}
        onChange={handleInputChange}
        placeholder={t("jobs.form.placeholders.location")}
      />
    </div>
  );

  const renderDepartmentInput = () => (
    <div className="form-group">
      <label className="form-label" htmlFor="department">
        {t("jobs.form.fields.department")}
      </label>
      <input
        type="text"
        id="department"
        name="department"
        className="form-input"
        value={formData.department}
        onChange={handleInputChange}
        placeholder={t("jobs.form.placeholders.department")}
      />
    </div>
  );

  const renderCollapsibleHeader = (sectionId: string, label: string) => {
    const collapsed = isCollapsed(sectionId);
    const summary = collapsed ? getSectionSummary(sectionId) : "";
    return (
      <button
        type="button"
        className={`panel-section-header ${collapsed ? "collapsed" : ""}`}
        onClick={() => toggleSection(sectionId)}
        aria-expanded={!collapsed}
        aria-controls={`section-content-${sectionId}`}
      >
        <span className="panel-section-label">{label}</span>
        {summary && <span className="panel-section-summary">{summary}</span>}
        <ChevronDown size={16} className="panel-section-chevron" />
      </button>
    );
  };

  const formContent = (
    <form onSubmit={handleSubmit} className="modal-form-inner">
      {errors.general && (
        <div className="error-message general-error">{errors.general}</div>
      )}

      <div className="modal-form-body">
        {/* LEFT PANEL: Content & Identity */}
        <div className="panel-left">
          {/* Title */}
          <div className="form-group">
            <label className="form-label" htmlFor="title">
              {t("jobs.form.fields.title")} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="title"
              name="title"
              className={`form-input ${errors.title ? "error" : ""}`}
              value={formData.title}
              onChange={handleInputChange}
              placeholder={t("jobs.form.placeholders.title")}
              required
            />
            {errors.title && (
              <span className="error-text">{errors.title}</span>
            )}
          </div>

          {/* Description */}
          <div className="form-group">
            <div className="label-row">
              <label className="form-label" htmlFor="description">
                {t("jobs.form.fields.description")}
              </label>
              {isEditing
                ? renderAiButton(
                    handleExtendDescription,
                    isExtendingDescription,
                    "AI: Improve and expand description",
                  )
                : null}
            </div>
            <textarea
              id="description"
              name="description"
              className={`form-textarea ${errors.description ? "error" : ""}`}
              value={formData.description}
              onChange={handleInputChange}
              rows={14}
              placeholder={t("jobs.form.placeholders.description")}
            />
            {errors.description && (
              <span className="error-text">{errors.description}</span>
            )}
            {!job && (
              <small className="form-hint">
                {t("jobs.form.hints.aiExtendHint")}
              </small>
            )}
          </div>

          {/* Main Skill */}
          <div className="form-group">
            <div className="label-row">
              <label className="form-label" htmlFor="main_skill">
                {t("jobs.form.fields.mainSkill")}
              </label>
              {renderAiButton(
                () => suggestMainSkillMutation.mutate(),
                suggestMainSkillMutation.isLoading,
                "AI: Suggest main skill from description",
              )}
            </div>
            <input
              type="text"
              id="main_skill"
              name="main_skill"
              className="form-input"
              value={formData.main_skill}
              onChange={handleInputChange}
              placeholder={t("jobs.form.placeholders.mainSkill")}
            />
            {errors.main_skill && (
              <span className="error-text">{errors.main_skill}</span>
            )}
          </div>

          {/* Skills */}
          <div className="form-group">
            <div className="label-row">
              <label className="form-label" htmlFor="skills">
                {t("jobs.form.fields.skills")}
              </label>
              {renderAiButton(
                () => suggestSkillsMutation.mutate(),
                suggestSkillsMutation.isLoading,
                "AI: Suggest skills based on description",
              )}
            </div>
            <TagInput
              value={formData.skills}
              onChange={(skills: string[]) =>
                setFormData((prev) => ({ ...prev, skills }))
              }
              placeholder={t("candidates.form.placeholders.skills")}
            />
            {errors.skills && (
              <span className="error-text">{errors.skills}</span>
            )}
          </div>

          {/* Tags */}
          <div className="form-group">
            <div className="label-row">
              <label className="form-label" htmlFor="tag-input">{t("jobs.form.fields.tags")}</label>
              {renderAiButton(
                handleGenerateTags,
                isGeneratingTags,
                "AI: Generate tags from description",
              )}
            </div>
            <div className="tag-input-container">
              {formData.tags.map((tag) => (
                <span key={tag} className="tag-pill">
                  {tag}
                  <button
                    type="button"
                    className="tag-remove"
                    onClick={() => handleRemoveTag(tag)}
                    aria-label={`Remove tag ${tag}`}
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
              <input
                id="tag-input"
                type="text"
                className="tag-input"
                placeholder={t("jobs.form.placeholders.tags")}
                value={tagInputValue}
                onChange={(e) => setTagInputValue(e.target.value)}
                onKeyDown={handleTagKeyDown}
              />
            </div>
            {errors.tags && <span className="error-text">{errors.tags}</span>}
          </div>

          {/* Customer Rate + Engineer Rate */}
          <div className="form-row-2col">
            <div className="form-group">
              <label className="form-label" htmlFor="customer_rate">
                {t("jobs.form.fields.customerRate")}
              </label>
              <div className="input-with-suffix">
                <input
                  type="number"
                  id="customer_rate"
                  name="customer_rate"
                  className={`form-input ${errors.customer_rate ? "error" : ""}`}
                  value={formData.customer_rate}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  step="0.01"
                />
                <span className="input-suffix">&euro;/hr</span>
              </div>
              {errors.customer_rate && (
                <span className="error-text">{errors.customer_rate}</span>
              )}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="engineer_rate">
                {t("jobs.form.fields.engineerRate")}
              </label>
              <div className="input-with-suffix">
                <input
                  type="number"
                  id="engineer_rate"
                  name="engineer_rate"
                  className={`form-input ${errors.engineer_rate ? "error" : ""}`}
                  value={formData.engineer_rate}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  step="0.01"
                />
                <span className="input-suffix">&euro;/hr</span>
              </div>
              {errors.engineer_rate && (
                <span className="error-text">{errors.engineer_rate}</span>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT PANEL: Metadata */}
        <div className="panel-right">
          {/* Basic Info (collapsible) */}
          {renderCollapsibleHeader(
            "basic-info",
            t("jobs.form.sections.basicInfo"),
          )}
          <div
            id="section-content-basic-info"
            className={`panel-section-content ${isCollapsed("basic-info") ? "collapsed" : ""}`}
          >
            {isAgencyMode ? (
              <>
                <div className="form-row-2col">
                  {renderCustomerSelect()}
                  {renderPrioritySelect()}
                </div>
                <div className="form-row-2col">
                  {renderEmploymentTypeSelect()}
                  {renderSenioritySelect()}
                </div>
                <div className="form-row-2col">
                  {renderLocationInput()}
                  {renderDepartmentInput()}
                </div>
              </>
            ) : (
              <>
                <div className="form-row-2col">
                  {renderPrioritySelect()}
                  {renderEmploymentTypeSelect()}
                </div>
                <div className="form-row-2col">
                  {renderSenioritySelect()}
                  {renderLocationInput()}
                </div>
                {renderDepartmentInput()}
              </>
            )}
          </div>

          {/* Timeline (collapsible) */}
          {renderCollapsibleHeader(
            "timeline",
            t("jobs.form.sections.timeline"),
          )}
          <div
            id="section-content-timeline"
            className={`panel-section-content ${isCollapsed("timeline") ? "collapsed" : ""}`}
          >
            <div className="form-row-2col">
              <div className="form-group">
                <label className="form-label" htmlFor="start_date">
                  {t("jobs.form.fields.startDate")}
                </label>
                <input
                  type="date"
                  id="start_date"
                  name="start_date"
                  className="form-input"
                  value={formData.start_date}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="duration_months">
                  {t("jobs.form.fields.durationMonths")}
                </label>
                <div className="input-with-suffix">
                  <input
                    type="number"
                    id="duration_months"
                    name="duration_months"
                    className="form-input"
                    value={formData.duration_months}
                    onChange={handleInputChange}
                    placeholder={t("jobs.form.placeholders.durationMonths")}
                    min="1"
                  />
                  <span className="input-suffix">months</span>
                </div>
              </div>
            </div>

            <div className="form-row-2col">
              <div className="form-group">
                <label className="form-label" htmlFor="work_model">
                  {t("jobs.form.fields.workModel")}
                </label>
                <select
                  id="work_model"
                  name="work_model"
                  className="form-select"
                  value={formData.work_model}
                  onChange={handleInputChange}
                >
                  <option value="">
                    {t("jobs.form.selects.selectWorkModel")}
                  </option>
                  <option value="On-site">On-site</option>
                  <option value="Hybrid">Hybrid</option>
                  <option value="Remote">Remote</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="positions">
                  {t("jobs.form.fields.positions")}
                </label>
                <input
                  type="number"
                  id="positions"
                  name="positions"
                  className="form-input"
                  value={formData.positions}
                  onChange={handleInputChange}
                  min="1"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="deadline">
                {t("jobs.form.fields.deadline")}
              </label>
              <input
                type="date"
                id="deadline"
                name="deadline"
                className="form-input"
                value={formData.deadline}
                onChange={handleInputChange}
              />
            </div>
          </div>

          {/* Assignment (non-collapsible) */}
          <div className="panel-section-label">
            {t("jobs.form.sections.assignment")}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="responsible_sales_id">
              {t("jobs.form.fields.responsibleSales")}
            </label>
            <select
              id="responsible_sales_id"
              name="responsible_sales_id"
              className="form-select"
              value={formData.responsible_sales_id?.toString() || ""}
              onChange={handleInputChange}
            >
              <option value="">
                {t("jobs.form.selects.noResponsibleSales")}
              </option>
              {salesUsers.map((user) => (
                <option key={user.id} value={user.id.toString()}>
                  {user.username}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="responsible_recruiter_id">
              {t("jobs.form.fields.responsibleRecruiter")}
            </label>
            <select
              id="responsible_recruiter_id"
              name="responsible_recruiter_id"
              className="form-select"
              value={formData.responsible_recruiter_id?.toString() || ""}
              onChange={handleInputChange}
            >
              <option value="">
                {t("jobs.form.selects.noResponsibleRecruiter")}
              </option>
              {recruiters.map((user) => (
                <option key={user.id} value={user.id.toString()}>
                  {user.username}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group checkbox-group">
            <input
              type="checkbox"
              className="form-checkbox"
              id="openForRecruiters"
              name="open_for_recruiters"
              checked={formData.open_for_recruiters}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  open_for_recruiters: e.target.checked,
                })
              }
            />
            <label className="checkbox-label" htmlFor="openForRecruiters">
              {t("jobs.form.fields.openForRecruiters")}
            </label>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="modal-form-footer job-form-footer">
        {isEditing && onDelete && (
          <button type="button" onClick={handleDelete} className="btn-delete">
            {t("common:actions.delete")}
          </button>
        )}
        <div className="footer-actions">
          <button type="button" onClick={onCancel} className="btn-cancel">
            {t("common:actions.cancel")}
          </button>
          <button
            type="submit"
            className="btn-create"
            disabled={saveJobMutation.isLoading}
          >
            <Briefcase size={18} />
            {saveJobMutation.isLoading
              ? t("common:actions.saving")
              : isEditing
                ? t("jobs.form.buttons.updateJob")
                : t("jobs.form.buttons.createJob")}
          </button>
        </div>
      </div>

      <TagSuggestionsModal
        isOpen={showTagSuggestions}
        onClose={() => setShowTagSuggestions(false)}
        suggestions={tagSuggestions}
        onAddTags={handleAddSuggestedTags}
        isLoading={isGeneratingTags}
      />
    </form>
  );

  if (variant === "inline") {
    return <div className="job-form job-form-inline modal-form">{formContent}</div>;
  }

  return (
    <div className="job-form job-form-modal modal-form">
      <div className="modal-form-header">
        <h2>
          {isEditing ? t("jobs.form.titleEdit") : t("jobs.form.titleNew")}
        </h2>
        <button
          type="button"
          className="modal-close"
          onClick={onCancel}
          aria-label="Close"
        >
          <X size={20} />
        </button>
      </div>
      {formContent}
    </div>
  );
};

export default JobForm;
